import { addDays, format } from 'date-fns';
import { log } from 'evlog/client';
import { CalendarPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { GetReservationById } from '@virtual-care/client';
import { useEarliestBookableDate } from '@virtual-care/hooks';
import { Button } from '@virtual-care/shadcn';
import {
  FollowupScheduleDialog,
  type UiTimeSlot,
  apiErrorToMessage,
  resolveErrorMessage,
  useAction,
} from '@virtual-care/ui';

import { useSetting } from '../../../hooks/use-settings.tsx';

export function FollowupButton({
  reservation,
  onSuccess,
}: {
  reservation: GetReservationById;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<UiTimeSlot | undefined>();
  const [error, setError] = useState<string | undefined>();

  const earliestBookableDate = useEarliestBookableDate();
  const [date, setDate] = useState<Date | undefined>(earliestBookableDate);

  const markedForFollowupEvent = reservation.timeline.find(
    (event) => event.type === 'MARKED_AVAILABLE_FOR_FOLLOWUP',
  );

  const followupWindowDaysSetting = useSetting('followupWindowDays');
  const followupWindowDays = followupWindowDaysSetting
    ? parseInt(followupWindowDaysSetting.value, 10)
    : 14;

  const followupWindowExpiry = markedForFollowupEvent
    ? addDays(new Date(markedForFollowupEvent.createdAt), followupWindowDays)
    : undefined;

  const scheduleFollowup = useAction(
    'PATCH /reservations/{id}/schedule-followup',
    {
      invalidate: ['GET /patients/me/reservations', 'GET /reservations/{id}'],
    },
  );

  const handleScheduleFollowup = async () => {
    log.info({ action: 'schedule_followup', reservationId: reservation.id });
    if (!date || !selectedSlot) {
      toast.error(t('appointments.actions.followup.validationRequired'));
      return;
    }

    setError(undefined);
    await scheduleFollowup.mutateAsync(
      {
        id: reservation.id,
        scheduledMinutes: selectedSlot.startTime,
        scheduledDate: `${format(selectedSlot.datetime, 'yyyy-MM-dd')}T00:00:00.000Z`,
      },
      {
        onSuccess: () => {
          toast.success(t('appointments.actions.followup.success'));
          onSuccess();
          setIsDialogOpen(false);
          setDate(earliestBookableDate);
          setSelectedSlot(undefined);
        },
        onError: (err) => {
          setError(resolveErrorMessage(apiErrorToMessage(err)));
        },
      },
    );
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-start gap-3">
        <Button
          variant="default"
          className="cursor-pointer gap-2 bg-green-600 text-sm font-bold text-white hover:bg-green-700"
          onClick={() => setIsDialogOpen(true)}
        >
          {t('appointments.actions.followup.trigger')}{' '}
          <CalendarPlus className="h-5 w-5" />
        </Button>
      </div>

      <FollowupScheduleDialog
        reservation={reservation}
        open={isDialogOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setDate(earliestBookableDate);
            setSelectedSlot(undefined);
            setError(undefined);
          }
          setIsDialogOpen(isOpen);
        }}
        reservationNumber={reservation.reservationNumber}
        date={date}
        selectedSlot={selectedSlot}
        onDateChange={setDate}
        onSlotSelect={setSelectedSlot}
        onConfirm={handleScheduleFollowup}
        isConfirmPending={scheduleFollowup.isPending}
        defaultMonth={earliestBookableDate}
        disabledDates={{
          before: earliestBookableDate,
          after: followupWindowExpiry,
        }}
        error={error}
      />
    </>
  );
}
